import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  type CitationParts,
  mlaDate,
  type PartialDate,
  parsePartialDate,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import {
  closer,
  englishOrdinal,
  get,
  italic,
  type LegalValues,
  legalOrdinal,
  mlaUrl,
  plain,
  wholeNumber,
} from "@/lib/tool-kit/legal-sources/common";

/*
 * Unenacted U.S. federal bills and resolutions in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA 7 §11.6, "Legislative Materials" (Publication Manual pp. 363–365), as reproduced
 *   by Liberty University and Columbus State: "Title [if relevant], H.R. or S.
 *   bill/resolution number, xxx Cong. (Year). URL" — "Mental Health on Campus
 *   Improvement Act, H.R. 1100, 113th Cong. (2013)." In text: (Mental Health on Campus
 *   Improvement Act, 2013). APA spells out the chamber in narrative citations ("Senate
 *   Resolution 438"). An enacted bill is cited as a statute.
 * - Bluebook 22d ed. rule 13.2 and table T9 (Tarlton Law Library; LLSDC "Common
 *   Abbreviations and Legal Citation Examples"): "Orphan Works Act of 2008, H.R. 5889,
 *   110th Cong. § 2 (2008)." Resolutions: H.R. Res., S. Res., H.R.J. Res., S.J. Res.,
 *   H.R. Con. Res., S. Con. Res. Ordinals in citations are "2d" and "3d" (rule 6.2(b)).
 * - Chicago 18 §14.184, following the Bluebook: "Restore Our Parks and Public Lands
 *   Act, H.R. 1225, 116th Cong. (2019)." (Lone Star College CMOS 18 guide); bills are
 *   cited in notes only (University of Portland CMOS 18 guide).
 * - MLA 9 Handbook §5.118 and the MLA Style Center ("Documenting Legal Works"):
 *   "United States, Congress, House. Improving Broadband Access for Veterans Act of
 *   2016. Congress.gov, www.congress.gov/bill/114th-congress/house-bill/6394/text. 114th
 *   Congress, 2nd session, House Resolution 6394, passed 6 Dec. 2016." The handbook asks
 *   for the Congress, session, chamber and the type and number of the publication; the
 *   Style Center's example calls H.R. 6394 a "House Resolution", but an H.R. number is a
 *   bill, so this generator names the type correctly ("House Bill 6394").
 */

export const BILL_FIELDS = {
  title: "title",
  type: "type",
  number: "number",
  congress: "congress",
  session: "session",
  date: "date",
  section: "section",
  website: "website",
  url: "url",
} as const;

const F = BILL_FIELDS;

export type BillType =
  | "hr"
  | "s"
  | "hres"
  | "sres"
  | "hjres"
  | "sjres"
  | "hconres"
  | "sconres";

/** Bluebook table T9 abbreviation, the spelled-out type, and the chamber. */
export const BILL_TYPES: Readonly<
  Record<BillType, { legal: string; spelled: string; chamber: string }>
> = {
  hr: { legal: "H.R.", spelled: "House Bill", chamber: "House" },
  s: { legal: "S.", spelled: "Senate Bill", chamber: "Senate" },
  hres: { legal: "H.R. Res.", spelled: "House Resolution", chamber: "House" },
  sres: { legal: "S. Res.", spelled: "Senate Resolution", chamber: "Senate" },
  hjres: {
    legal: "H.R.J. Res.",
    spelled: "House Joint Resolution",
    chamber: "House",
  },
  sjres: {
    legal: "S.J. Res.",
    spelled: "Senate Joint Resolution",
    chamber: "Senate",
  },
  hconres: {
    legal: "H.R. Con. Res.",
    spelled: "House Concurrent Resolution",
    chamber: "House",
  },
  sconres: {
    legal: "S. Con. Res.",
    spelled: "Senate Concurrent Resolution",
    chamber: "Senate",
  },
};

export interface BillLabels {
  apa: string;
  mla: string;
  chicago: string;
  referenceList: string;
  parenthetical: string;
  narrative: string;
  worksCited: string;
  inText: string;
  footnote: string;
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface Bill {
  title: string;
  type: (typeof BILL_TYPES)[BillType];
  number: string;
  congress: number | null;
  session: number | null;
  date: PartialDate | null;
  section: string;
  website: string;
  url: string;
}

const RE_SECTION_PREFIX = /^(§|sec\.?|section)\s*/i;

function readBill(values: LegalValues): Bill {
  const typeKey = (get(values, F.type) || "hr") as BillType;
  return {
    title: get(values, F.title),
    type: BILL_TYPES[typeKey] ?? BILL_TYPES.hr,
    number: get(values, F.number),
    congress: wholeNumber(get(values, F.congress)),
    session: wholeNumber(get(values, F.session)),
    date: parsePartialDate(get(values, F.date)),
    section: get(values, F.section).replace(RE_SECTION_PREFIX, ""),
    website: get(values, F.website),
    url: get(values, F.url),
  };
}

/** "Mental Health on Campus Improvement Act, H.R. 1100, 113th Cong. (2013)" */
function legalCite(bill: Bill): string {
  const designation = `${bill.type.legal} ${bill.number}`;
  const congress = bill.congress ? `${legalOrdinal(bill.congress)} Cong.` : "";
  const section = bill.section ? ` § ${bill.section}` : "";
  const year = bill.date ? ` (${bill.date.year})` : "";
  const core = [designation, `${congress}${section}${year}`]
    .filter(Boolean)
    .join(", ");
  return bill.title ? `${bill.title}, ${core}` : core;
}

function apaOutput(bill: Bill, labels: BillLabels): CitationOutput {
  const year = bill.date ? String(bill.date.year) : "n.d.";
  const name = bill.title || `${bill.type.legal} ${bill.number}`;
  const spoken = bill.title || `${bill.type.spelled} ${bill.number}`;
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      {
        label: labels.referenceList,
        parts: [plain(`${legalCite(bill)}.${bill.url ? ` ${bill.url}` : ""}`)],
      },
      { label: labels.parenthetical, parts: [plain(`(${name}, ${year})`)] },
      { label: labels.narrative, parts: [plain(`${spoken} (${year})`)] },
    ],
    note: labels.apaNote,
  };
}

/** "113th Congress, 1st session, House Bill 1100, introduced 12 Mar. 2013" */
function mlaOptionalElement(bill: Bill): string {
  const congress = bill.congress
    ? `${englishOrdinal(bill.congress)} Congress`
    : "";
  const session = bill.session ? `${englishOrdinal(bill.session)} session` : "";
  const designation = bill.title ? `${bill.type.spelled} ${bill.number}` : "";
  const introduced = bill.date ? `introduced ${mlaDate(bill.date)}` : "";
  return [congress, session, designation, introduced]
    .filter(Boolean)
    .join(", ");
}

function mlaOutput(bill: Bill, labels: BillLabels): CitationOutput {
  const title = bill.title || `${bill.type.spelled} ${bill.number}`;
  const parts: CitationParts = [
    plain(`United States, Congress, ${bill.type.chamber}. `),
    plain(`${title}${closer(title)} `),
  ];
  const location = mlaUrl(bill.url);
  if (bill.website) {
    parts.push(italic(bill.website));
    parts.push(plain(location ? `, ${location}. ` : ". "));
  } else if (location) {
    parts.push(plain(`${location}. `));
  }
  const optional = mlaOptionalElement(bill);
  if (optional) {
    parts.push(plain(`${optional}.`));
  }
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: tidyParts(parts) },
      { label: labels.inText, parts: [plain(`(United States, ${title})`)] },
    ],
    note: labels.mlaNote,
  };
}

export function billIsReady(values: LegalValues): boolean {
  return get(values, F.number) !== "" && get(values, F.congress) !== "";
}

export function formatBill(
  values: LegalValues,
  labels: BillLabels
): CitationOutput[] {
  const bill = readBill(values);
  const note = `${legalCite(bill)}${bill.url ? `, ${bill.url}` : ""}.`;
  return [
    apaOutput(bill, labels),
    mlaOutput(bill, labels),
    {
      key: "chicago",
      label: labels.chicago,
      entries: [{ label: labels.footnote, parts: [plain(note)] }],
      note: labels.chicagoNote,
    },
  ];
}
